import {
  Player,
  PlayerEvent,
} from "bitmovin-player/modules/bitmovinplayer-core";
import DrmModule from "bitmovin-player/modules/bitmovinplayer-drm";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import EngineNativeModule from "bitmovin-player/modules/bitmovinplayer-engine-native";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import AnalyticsModule from "bitmovin-player/modules/bitmovinplayer-analytics";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import Webos from "bitmovin-player/modules/bitmovinplayer-webos";
import Tizen from "bitmovin-player/modules/bitmovinplayer-tizen";
import Crypto from "bitmovin-player/modules/bitmovinplayer-crypto";

Player.addModule(DrmModule);
Player.addModule(PolyfillModule);
Player.addModule(EngineBitmovinModule);
Player.addModule(EngineNativeModule);
Player.addModule(HlsModule);
Player.addModule(XmlModule);
Player.addModule(DashModule);
Player.addModule(ContainerTSModule);
Player.addModule(ContainerMp4Module);
Player.addModule(AnalyticsModule);
Player.addModule(MseRendererModule);
Player.addModule(AbrModule);
Player.addModule(Webos);
Player.addModule(Tizen);
Player.addModule(Crypto);

const SITE_ID = "QL8D";
const PLAYER_KEY = process.env.VUE_APP_BITMOVIN_PLAYER_KEY;
const ANALYTICS_KEY = process.env.VUE_APP_BITMOVIN_ANALYTICS_KEY;
const DEFAULT_VOLUME = 50;
const PLAYER_ISSUER = "_player";
const APP_ID = process.env.VUE_APP_ID;

const configuration = {
  key: PLAYER_KEY,
  ui: false,
  playback: {
    autoplay: true,
    muted: false,
    // preferredTech: [{
    //     player: 'html5',
    //     streaming: 'hls'
    // }]
  },
  tweaks: {
    file_protocol: true,
    app_id: APP_ID,
  },
  advertising: {},
  buffer: {
    video: {
      forwardduration: 6,
      backwardduration: 4,
    },
  },
};

const analytics = {
  key: ANALYTICS_KEY,
  videoId: null,
};

// const customErrorMessageConfig = {
//   // 1102: '에러 메시지 여기서 설정',
// };

export class NowPlayer {
  seekValue = 5;
  #player;
  #loadPlayerConfig = {};
  #loadSourceConfig = {
    hls: null,
    hlsDvr: null,
    dash: null,
    dashDvr: null,
    isDvr: false,
    isDrm: false,
  };

  // confirmOverlay = null;

  muted = false;
  userMuted = false;
  started = false;
  error = false;

  constructor(targetElement, config = {}) {
    this.#loadPlayerConfig = config;
    this.#player = new Player(targetElement, {
      ...configuration,
      ...config,
      ...{ analytics: { ...analytics, ...config.analytics } },
    });

    this.#player.on(PlayerEvent.SourceLoaded, () => {
      this.started = true;
      this.error = false;
    });

    this.#player.on(PlayerEvent.Error, (data) => {
      this.started = false;
      this.error = true;
    });
  }

  /**
   * 최소 선택(재생) 가능한 화질 제한
   * ※ 1080 화질을 제공하지 않는 Live가 없어 1080을 고정 값으로 설정 (운영쪽 확인 완료)
   * @param height
   */
  setMinSelectableVideoHeight = (height) => {
    if (height > 0) {
      this.#player.adaptation.setConfig({
        resolution: {
          minSelectableVideoHeight: height,
        },
      });
    } else {
      this.#player.adaptation.setConfig({});
    }
  };

  getPlayer = () => {
    return this.#player;
  };

  isMuted = () => {
    return this.#player.isMuted();
  };

  isPaused = () => {
    return this.#player.isPaused();
  };

  onSourceLoaded = (callback) => {
    this.#player.on(PlayerEvent.SourceLoaded, callback);
  };

  onSourceUnloaded = (callback) => {
    this.#player.on(PlayerEvent.SourceUnloaded, callback);
  };

  onError = (callback) => {
    this.#player.on(PlayerEvent.Error, callback);
  };

  onPlay = (callback) => {
    this.#player.on(PlayerEvent.Play, callback);
  };

  onPlaying = (callback) => {
    this.#player.on(PlayerEvent.Playing, callback);
  };

  onPaused = (callback) => {
    this.#player.on(PlayerEvent.Paused, callback);
  };

  onPlaybackFinished = (callback) => {
    this.#player.on(PlayerEvent.PlaybackFinished, callback);
  };

  onSeeked = (callback) => {
    this.#player.on(PlayerEvent.Seeked, callback);
  };

  unload = () => {
    if (this.#player.getSource()) {
      this.#player.unload();
    }

    this.started = false;
    this.muted = false;
    this.userMuted = false;
  };

  moveTime = (time) => {
    this.#player.seek(time);
  };

  play = () => {
    this.#player.play();
  };

  pause = () => {
    this.#player.pause();
  };

  setVideoQuality = (videoQualityId) => {
    this.#player.setVideoQuality(videoQualityId);
  };

  getCurrentTime = () => {
    return this.#player.getCurrentTime();
  };

  getDuration = () => {
    return this.#player.getDuration();
  };

  getVideoQuality = () => {
    return this.#player.getVideoQuality();
  };

  getAvailableVideoQualities = () => {
    return this.#player.getAvailableVideoQualities();
  };

  destroy = () => {
    this.#player.destroy();
  };

  getSupportedDRM = () => {
    return this.#player.getSupportedDRM();
  };

  isDRMSupported = () => {
    return this.#player.isDRMSupported();
  };

  loadVideo = ({
    videoId,
    hls,
    hlsDvr,
    dash,
    dashDvr,
    progressive,
    isDrm = false,
    cid,
    userId,
    type = "live",
  }) => {
    this.#loadSourceConfig = {
      dash,
      hls,
      dashDvr,
      hlsDvr,
      isDrm,
      isDvr: isDrm ? !!hlsDvr || !!dashDvr : !!hlsDvr || !!dashDvr,
    };

    if (!this.#player) {
      throw new Error("player is required");
    }

    let source = {};

    if (dash) source.dash = dash;
    if (hls) source.hls = hls;
    if (progressive) source.progressive = progressive;
    if (isDrm && cid && userId) {
      source.drm = {
        widevine: {
          LA_URL: `${process.env.VUE_APP_DRM_LICENCE_PROXY_URL}/widevine/${cid}/${userId}`,
          audioRobustness: "SW_SECURE_CRYPTO",
          videoRobustness: "SW_SECURE_CRYPTO",
          headers: {},
        },
        // playready: {
        //     LA_URL: `${process.env.VUE_APP_DRM_LICENCE_PROXY_URL}/playready/${cid}/${userId}`,
        //     headers: {}
        // },
        fairplay: {
          LA_URL: `${process.env.VUE_APP_DRM_LICENCE_PROXY_URL}/fairplay/${cid}/${userId}`,
          certificateURL: `https://license-global.pallycon.com/ri/fpsKeyManager.do?siteId=${SITE_ID}`,
          headers: {},
          prepareContentId: function(contentId) {
            return contentId.substring(contentId.indexOf("skd://") + 6);
          },
          prepareCertificate: function(rawResponse) {
            let responseText = String.fromCharCode.apply(
              null,
              new Uint8Array(rawResponse),
            );
            let raw = window.atob(responseText);
            let rawLength = raw.length;
            let certificate = new Uint8Array(new ArrayBuffer(rawLength));

            for (let i = 0; i < rawLength; i++)
              certificate[i] = raw.charCodeAt(i);

            return certificate;
          },
          useUint16InitData: true,
        },
      };
    }

    // Bitmovin Analytics Setting
    if (this.#player.analytics?.config?.key && videoId) {
      this.#player.analytics.config.videoId = videoId;
    }

    return this.#player
      .load(source)
      .then(() => {
        console.log("bitmovin player load");
      })
      .catch((e) => {
        this.started = false;
        this.error = true;
        console.log(e);
        return e;
      });
  };
}
